import { useCallback, useMemo, useRef } from "react";
import { FileClock } from "lucide-react";

import type { AuditEvent } from "../../../models/audit/auditEvent";
import { Skeleton } from "../../ui/skeleton";
import { AuditEventRailRow } from "./AuditEventRailRow";
import { dayKey, formatDayLabel } from "./auditDates";

interface DayGroup {
  key: number;
  label: string;
  events: AuditEvent[];
}

/**
 * Buckets a newest-first feed into consecutive local-day groups, preserving
 * order. Events arrive sorted, so a group breaks whenever the day index
 * changes — no sorting or map lookup needed.
 */
function groupByDay(events: AuditEvent[], now: Date): DayGroup[] {
  return events.reduce<DayGroup[]>((groups, event) => {
    const date = new Date(event.createdAt);
    const key = dayKey(date);
    const current = groups[groups.length - 1];
    if (current && current.key === key) {
      current.events.push(event);
      return groups;
    }
    return [
      ...groups,
      { key, label: formatDayLabel(date, now), events: [event] },
    ];
  }, []);
}

function RailSkeleton() {
  return (
    <div className="space-y-4 px-6 py-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex gap-3">
          <Skeleton className="h-[22px] w-[22px] flex-shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5 pt-0.5">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface AuditEventRailProps {
  events: AuditEvent[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  selectedEventId: string | null;
  onSelect: (event: AuditEvent) => void;
  /** Rendered as the empty state when a category filter is active. */
  emptyLabel: string;
  onClearFilter?: () => void;
  memberEmailById?: (userId: string) => string | undefined;
  columnNameById?: (columnId: string) => string | undefined;
  /** Forwarded to each row: show the event's test/control origin, for
   * surfaces whose feed mixes tests (the workspace page). */
  showOrigin?: boolean;
}

/**
 * The drawer's left column: a day-grouped timeline with sticky date headings,
 * a continuous hairline connector per group, and infinite scroll via an
 * `IntersectionObserver` on the last row.
 */
export function AuditEventRail({
  events,
  loading,
  hasMore,
  loadMore,
  selectedEventId,
  onSelect,
  emptyLabel,
  onClearFilter,
  memberEmailById,
  columnNameById,
  showOrigin,
}: AuditEventRailProps) {
  const observer = useRef<IntersectionObserver | null>(null);
  const lastRowRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMore],
  );

  // Recomputed only when the feed changes; `now` is captured per render of a
  // new feed so "Today" stays correct across a long-lived drawer session.
  const groups = useMemo(() => groupByDay(events, new Date()), [events]);
  const lastEventId = events.length > 0 ? events[events.length - 1].id : null;

  if (loading && events.length === 0) {
    return <RailSkeleton />;
  }

  if (!loading && events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-stone-100">
          <FileClock className="h-5 w-5 text-stone-400" />
        </span>
        <p className="text-xs text-stone-500">{emptyLabel}</p>
        {onClearFilter && (
          <button
            type="button"
            onClick={onClearFilter}
            className="text-xs font-medium text-purple-600 hover:text-purple-700 hover:underline"
          >
            Clear filter
          </button>
        )}
      </div>
    );
  }

  return (
    // px-4 + the day header's / rows' own px-2 left padding puts their text
    // and glyphs at 24px, flush with the sheet header's px-6 "Audit Log".
    <div className="px-4 pb-4">
      {groups.map((group) => (
        <div key={group.key}>
          <div className="sticky top-0 z-20 bg-white/95 px-2 py-2 text-[10px] font-medium uppercase tracking-wider text-font-tertiary backdrop-blur">
            {group.label}
          </div>
          {/* ml-2 lands the rows' left border — where the selection accent
              bar sits — on the same 24px line as the day label above. */}
          <div className="relative ml-2">
            <div className="absolute bottom-3 left-[19px] top-3 w-px bg-stone-200" />
            {group.events.map((event) => (
              <div
                key={event.id}
                ref={event.id === lastEventId ? lastRowRef : undefined}
              >
                <AuditEventRailRow
                  event={event}
                  selected={event.id === selectedEventId}
                  onSelect={() => onSelect(event)}
                  memberEmailById={memberEmailById}
                  columnNameById={columnNameById}
                  showOrigin={showOrigin}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      {loading && (
        <div className="flex justify-center py-3">
          <Skeleton className="h-3 w-24" />
        </div>
      )}
    </div>
  );
}
